import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/utils/tailwind';

import {
  ArrowRightIcon,
  AzureLogoIcon,
  BambooLogoIcon,
  ChatIcon,
  CheckBadgeIcon,
  CheckSmallIcon,
  GoogleLogoIcon,
  KeyIcon,
  OktaLogoIcon,
  SettingsIcon,
  SlackLogoIcon,
  TeamsLogoIcon,
  UsersIcon,
  WorkdayLogoIcon,
} from '../../../assets/icons';
import { Button, Card, Tag } from '../../atoms';
import { CardContent } from '../../atoms/Card/Card';

export type IntegrationCardButtonLabelType = 'verify' | 'configure' | 'connect' | 'manage';

export type IntegrationCardProps = {
  title: string;
  logoVariant?:
    | 'bamboo'
    | 'slack'
    | 'google'
    | 'okta'
    | 'azure'
    | 'workday'
    | 'teams'
    | 'domain'
    | 'provider'
    | 'hris'
    | 'sso'
    | 'chat';
  type?: 'default' | 'connected' | 'not-connected';
  label?: string;
  showConnectionStatus?: boolean;
  showAction?: boolean;
  buttonLabelType?: IntegrationCardButtonLabelType;
  disabled?: boolean;
  squareImage?: string;
  onClick?: () => void;
};

export const IntegrationCard: FC<IntegrationCardProps> = ({
  title,
  logoVariant,
  type = 'not-connected',
  buttonLabelType,
  disabled,
  label,
  showConnectionStatus,
  showAction = true,
  squareImage,
  onClick,
}) => {
  const { t } = useTranslation('components');

  const iconByLogoVariant = useMemo(() => {
    if (squareImage) {
      return <img src={squareImage} alt={title} className="size-6 rounded-full" />;
    }

    switch (logoVariant) {
      case 'bamboo': {
        return <BambooLogoIcon />;
      }
      case 'slack': {
        return <SlackLogoIcon />;
      }
      case 'google': {
        return <GoogleLogoIcon />;
      }
      case 'okta': {
        return <OktaLogoIcon />;
      }
      case 'azure': {
        return <AzureLogoIcon />;
      }
      case 'workday': {
        return <WorkdayLogoIcon />;
      }
      case 'teams': {
        return <TeamsLogoIcon />;
      }
      case 'domain': {
        return <CheckBadgeIcon className="text-custom-cyan-500" />;
      }
      case 'provider': {
        return <SettingsIcon className="text-custom-cyan-500" />;
      }
      case 'hris': {
        return <UsersIcon className="text-custom-cyan-500" />;
      }
      case 'sso': {
        return <KeyIcon className="text-custom-cyan-500" />;
      }
      case 'chat': {
        return <ChatIcon className="text-custom-cyan-500" />;
      }
      default: {
        return null;
      }
    }
  }, [logoVariant, squareImage, title]);

  const buttonLabelByType = useMemo(() => {
    if (!buttonLabelType) {
      return '';
    }

    if (type === 'connected') {
      switch (buttonLabelType) {
        case 'verify': {
          return t('integrationCard.status.connected.verified');
        }
        case 'configure': {
          return t('integrationCard.status.connected.configured');
        }
        case 'connect': {
          return t('integrationCard.status.connected.connected');
        }
        case 'manage': {
          return t('integrationCard.status.connected.managed');
        }
      }
    } else {
      switch (buttonLabelType) {
        case 'verify': {
          return t('integrationCard.status.notConnected.verify');
        }
        case 'configure': {
          return t('integrationCard.status.notConnected.configure');
        }
        case 'connect': {
          return t('integrationCard.status.notConnected.connect');
        }
        case 'manage': {
          return t('integrationCard.status.notConnected.manage');
        }
      }
    }
  }, [buttonLabelType, type, t]);

  const tagByType = useMemo(() => {
    switch (type) {
      case 'connected': {
        return (
          <div className="flex items-center gap-2 mb-2">
            <p className="text-body-14-regular text-custom-warm-grey-500">{label}</p>
            <Tag label={`${t('integrationCard.connected')}`} size="regular" color="green" />
          </div>
        );
      }
      case 'not-connected': {
        return (
          <div className="flex items-center gap-2 mb-2">
            <p className="text-body-14-regular text-custom-warm-grey-500">{label}</p>
            <Tag label={`${t('integrationCard.not_connected')}`} size="regular" color="red" />
          </div>
        );
      }
      case 'default': {
        return null;
      }
    }
  }, [type, label, t]);

  return (
    <Card
      className={cn('rounded-[12px]', {
        'pointer-events-none opacity-50': disabled,
      })}
    >
      <CardContent className="flex items-center">
        <div className="p-[14px] mr-3 rounded-[8px] flex items-center justify-center] bg-custom-warm-grey-100">
          {iconByLogoVariant}
        </div>
        <div className="grow mr-5">
          {showConnectionStatus && tagByType}
          {title ? <p className="text-body-16-medium text-custom-warm-grey-800">{title}</p> : null}
        </div>
        {showAction ? (
          <div
            className={cn({
              hidden: disabled,
            })}
          >
            {type === 'connected' ? (
              <Tag
                label={buttonLabelByType}
                leftIcon={<CheckSmallIcon width={20} height={20} />}
                size="large"
                className="px-2 py-1"
              />
            ) : (
              <Button
                size="md"
                variant="secondary"
                rightIcon={<ArrowRightIcon width={20} height={20} />}
                onClick={onClick}
              >
                {buttonLabelByType}
              </Button>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};
